import React, { useEffect } from "react";
import { logPageView, logEvent } from "../lib/firebase";

const APP_ICON =
  "https://customer-assets.emergentagent.com/job_2e49d4d9-f3e8-4148-8ce0-ef89b2cdd70b/artifacts/slbhfg1u_BrainGrowth_AppIcon%20%281%29.png";
const BRAINPET_LOGO =
  "https://customer-assets.emergentagent.com/job_2e49d4d9-f3e8-4148-8ce0-ef89b2cdd70b/artifacts/e7zjhoe6_logo_brainPet.png";
const SPLASH_BG =
  "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/rr0p46hf_gemini-watermark-removed.png";
const SPLASH_PERSONA =
  "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/x9t0tpl8_persona.png";
const PLAY_BADGE =
  "https://customer-assets.emergentagent.com/job_2e49d4d9-f3e8-4148-8ce0-ef89b2cdd70b/artifacts/ps6g2ggy_Google_Play_Store_badge_EN.svg.webp";
const PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.VoltGames.BrainGrowth";

// Teal stage color sampled from bg_persona, used as a seamless fallback behind cover
const STAGE_BG = "#0E8478";

const FEATURES = [
  {
    id: "daily-trainings",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/j8cp0enk_characterpose02.png",
    title: "Daily Trainings",
    desc: "Turn quick games into a daily habit — train focus, memory and logic anytime, anywhere.",
    bg: "#D9F5EC",
    accent: "#0E8478",
  },
  {
    id: "mind-companion",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/azesae2o_characterpose06.png",
    title: "Mind Companion",
    desc: "Your pet buddy grows and levels up right alongside you as your cognitive skills sharpen.",
    bg: "#C5EAE3",
    accent: "#0A5D54",
  },
  {
    id: "learn-yourself",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/p4rokilm_characterpose04.png",
    title: "Learn About Yourself",
    desc: "Take guided tests that uncover your strengths, habits and the way your mind likes to work.",
    bg: "#E4F0D3",
    accent: "#5C8A2A",
  },
  {
    id: "stay-honest",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/arum4tks_characterpose03.png",
    title: "Stay Honest",
    desc: "Add friends, share streaks, and keep each other motivated with friendly accountability.",
    bg: "#CFEFE0",
    accent: "#1F9E7B",
  },
  {
    id: "see-results",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/anr8z6c4_characterpose05.png",
    title: "See Results",
    desc: "Watch your scores climb and your cognitive skills measurably improve over time.",
    bg: "#EDF3BE",
    accent: "#7E9A1E",
  },
  {
    id: "discover-type",
    img: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/br0d2rzj_characterpose01.png",
    title: "Discover Your Type",
    desc: "Take MBTI-style, DISC and Left vs Right Brain assessments to understand how you think, decide and connect.",
    bg: "#FFE2DA",
    accent: "#C2533F",
  },
];

const SCREENSHOTS = [
  {
    id: "shot-daily",
    src: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/yl7bhbd7_1%20%282%29.jpg",
    alt: "Daily Trainings screen — Odd One Out, Item Count, Basic Math tasks",
  },
  {
    id: "shot-companion",
    src: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/qkyyn22f_5.jpg",
    alt: "Mind Companion screen — pet leveling up and progress categories",
  },
  {
    id: "shot-tests",
    src: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/gks68zsr_2%20%282%29.jpg",
    alt: "Learn About Yourself — DISC personality recommended test",
  },
  {
    id: "shot-friends",
    src: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/gfd4gxjz_3%20%281%29.jpg",
    alt: "Stay Honest — friends leaderboard with streaks",
  },
  {
    id: "shot-results",
    src: "https://customer-assets.emergentagent.com/job_brainpet-landing/artifacts/3oypuop3_4%20%283%29.jpg",
    alt: "See Results — Basic Math high score and peer comparison",
  },
];

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#screenshots", label: "Peek Inside" },
];


export default function Landing() {
  useEffect(() => {
    logPageView("/");
  }, []);

  const trackPlayClick = (placement) => {
    logEvent("click_google_play", {
      placement,
      destination: PLAY_URL,
    });
  };

  return (
    <main
      data-testid="brainpet-landing"
      className="relative w-full overflow-x-hidden bg-[#F0F8F2] text-[#0F2E26]"
    >
      {/* ============ FLOATING NAV (sticky over hero + rest of site) ============ */}
      <nav
        data-testid="site-nav"
        className="fixed inset-x-0 top-0 z-50 mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 pt-5 sm:px-10 sm:pt-6"
        style={{ fontFamily: "Nunito, sans-serif" }}
      >
        {/* Brand pill */}
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-3 rounded-full bg-white/95 px-3 py-2 shadow-md ring-1 ring-black/5 backdrop-blur transition-transform duration-300 hover:-translate-y-0.5"
        >
          <img
            src={APP_ICON}
            alt="BrainPet app icon"
            className="h-9 w-9 rounded-xl ring-1 ring-black/5"
          />
          <span
            className="pr-2 text-lg font-extrabold tracking-tight text-[#0F2E26]"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            BrainPet
          </span>
        </a>

        {/* Center nav links pill */}
        <div className="hidden items-center gap-1 rounded-full bg-white/90 px-2 py-1.5 shadow-md ring-1 ring-black/5 backdrop-blur md:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              href={l.href}
              className="rounded-full px-4 py-2 text-sm font-bold text-[#0F2E26] transition-colors duration-200 hover:bg-[#D9F5EC] hover:text-[#0E8478]"
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* CTA pill */}
        <a
          data-testid="nav-play-link"
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackPlayClick("nav")}
          className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#0E8478] shadow-lg ring-1 ring-black/5 transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#FFF5EB]"
        >
          Get the app
        </a>
      </nav>

      <span id="top" aria-hidden="true" />
      {/* ============ HERO / SPLASH (bamboo stage + graduation persona) ============ */}
      <section
        data-testid="hero-section"
        className="relative h-[100svh] min-h-[660px] w-full overflow-hidden"
        style={{ backgroundColor: STAGE_BG }}
      >
        {/* Bamboo stage background */}
        <img
          src={SPLASH_BG}
          alt=""
          aria-hidden="true"
          data-testid="splash-bg"
          className="absolute inset-0 h-full w-full select-none object-cover"
          style={{ objectPosition: "center bottom" }}
          fetchpriority="high"
        />

        {/* Soft top scrim removed — floating nav handles its own legibility */}

        {/* BrainPet logo — top center, above the persona */}
        <img
          src={BRAINPET_LOGO}
          alt="BrainPet"
          data-testid="game-logo"
          className="absolute left-1/2 top-[16%] z-10 h-14 w-auto -translate-x-1/2 select-none sm:top-[14%] sm:h-20 lg:top-[12%] lg:h-24"
          style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.35))" }}
        />

        {/* Graduation persona with floating icons — centered, standing on the stage arc */}
        <img
          src={SPLASH_PERSONA}
          alt="BrainPet graduation pet with brain-training mini-games orbiting around"
          data-testid="splash-persona"
          className="absolute left-1/2 bottom-[8%] z-[5] h-[68%] w-auto max-w-[92vw] -translate-x-1/2 select-none object-contain sm:bottom-[10%] sm:h-[72%]"
          style={{ filter: "drop-shadow(0 30px 40px rgba(0,0,0,0.25))" }}
        />

        {/* Google Play badge — centered at the bottom */}
        <div
          data-testid="hero-overlay"
          className="absolute inset-x-0 bottom-0 z-10 mx-auto flex max-w-7xl flex-col items-center gap-4 px-5 pb-8 sm:px-10 sm:pb-10"
        >
          <a
            data-testid="google-play-button"
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackPlayClick("hero")}
            className="group inline-flex items-center transition-transform duration-300 hover:-translate-y-1"
            aria-label="Get BrainPet on Google Play"
          >
            <img
              src={PLAY_BADGE}
              alt="Get it on Google Play"
              className="h-16 w-auto drop-shadow-xl sm:h-[68px]"
            />
          </a>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section
        data-testid="features-section"
        id="features"
        className="relative mx-auto max-w-7xl px-5 py-20 sm:px-10 sm:py-28"
      >
        <div className="mb-16 max-w-2xl">
          <span
            className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0E8478] shadow-sm ring-1 ring-black/5"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#0E8478]" />
            Features
          </span>
          <h2
            data-testid="features-headline"
            className="text-4xl font-black leading-tight tracking-tight sm:text-5xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Everything you need to
            <br />
            <span className="text-[#0E8478]">sharpen your mind.</span>
          </h2>
          <p
            className="mt-5 max-w-xl text-base leading-relaxed text-[#3F5A53] sm:text-lg"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Bite-sized games, puzzles, personality tests and a pet companion
            that cheers you on, every step of the way.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, idx) => (
            <article
              key={f.id}
              data-testid={`feature-card-${f.id}`}
              className="group relative flex flex-col items-center text-center"
            >
              {/* Soft radial blob behind the pet (no card container) */}
              <div
                className="relative mb-6 flex aspect-square w-full max-w-[300px] items-center justify-center"
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-[10%] rounded-full blur-2xl transition-transform duration-500 group-hover:scale-105"
                  style={{
                    background: `radial-gradient(circle at 50% 55%, ${f.bg} 0%, ${f.bg}00 70%)`,
                    opacity: 0.95,
                  }}
                />
                {/* sparkle accents */}
                <span
                  aria-hidden="true"
                  className="absolute left-[12%] top-[18%] h-2 w-2 rotate-45 rounded-sm opacity-50"
                  style={{ backgroundColor: f.accent }}
                />
                <span
                  aria-hidden="true"
                  className="absolute right-[16%] top-[26%] h-1.5 w-1.5 rotate-45 rounded-sm opacity-40"
                  style={{ backgroundColor: f.accent }}
                />
                <span
                  aria-hidden="true"
                  className="absolute bottom-[18%] left-[22%] h-1.5 w-1.5 rotate-45 rounded-sm opacity-35"
                  style={{ backgroundColor: f.accent }}
                />
                <img
                  src={f.img}
                  alt={f.title}
                  loading="lazy"
                  className="relative z-[1] h-[88%] w-auto object-contain drop-shadow-[0_24px_24px_rgba(15,46,38,0.18)] transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:scale-[1.04]"
                />
              </div>

              <span
                data-testid={`feature-step-${f.id}`}
                className="mb-2 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em]"
                style={{
                  fontFamily: "Nunito, sans-serif",
                  color: f.accent,
                }}
              >
                <span className="tabular-nums opacity-80">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="h-px w-6"
                  style={{ backgroundColor: f.accent, opacity: 0.45 }}
                />
              </span>
              <h3
                className="text-2xl font-extrabold tracking-tight"
                style={{
                  fontFamily: "Fraunces, serif",
                  color: f.accent,
                }}
              >
                {f.title}
              </h3>
              <p
                className="mt-2 max-w-xs text-sm leading-relaxed text-[#3F5A53] sm:text-base"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                {f.desc}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ============ SCREENSHOTS ============ */}
      <section
        data-testid="screenshots-section"
        id="screenshots"
        className="relative mx-auto max-w-7xl px-5 py-20 sm:px-10 sm:py-28"
      >
        <div className="mb-12 max-w-2xl">
          <span
            className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0E8478] shadow-sm ring-1 ring-black/5"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#0E8478]" />
            Peek inside
          </span>
          <h2
            data-testid="screenshots-headline"
            className="text-4xl font-black leading-tight tracking-tight sm:text-5xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            See BrainPet
            <br />
            <span className="text-[#0E8478]">in action.</span>
          </h2>
          <p
            className="mt-5 max-w-xl text-base leading-relaxed text-[#3F5A53] sm:text-lg"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Swipe through real screens from the game: daily trainings,
            personality tests, leaderboards and levelling up your pet.
          </p>
        </div>

        {/* Horizontal scroll-snap gallery */}
        <div
          data-testid="screenshots-gallery"
          className="-mx-5 overflow-x-auto pb-6 sm:-mx-10"
          style={{
            scrollSnapType: "x mandatory",
            scrollPaddingLeft: "1.25rem",
            scrollbarWidth: "thin",
            scrollbarColor: "#0E8478 transparent",
          }}
        >
          <ul className="flex w-max gap-5 px-5 sm:gap-6 sm:px-10">
            {SCREENSHOTS.map((s) => (
              <li
                key={s.id}
                data-testid={`screenshot-${s.id}`}
                className="shrink-0"
                style={{ scrollSnapAlign: "start" }}
              >
                <div className="overflow-hidden rounded-[28px] bg-white ring-1 ring-black/5 shadow-[0_24px_50px_-30px_rgba(14,132,120,0.55)] transition-transform duration-300 hover:-translate-y-1">
                  <img
                    src={s.src}
                    alt={s.alt}
                    loading="lazy"
                    className="block h-[380px] w-[320px] object-cover object-top sm:h-[430px] sm:w-[360px]"
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA strip */}
        <div
          data-testid="mid-cta"
          className="mt-20 flex flex-col items-center gap-5 rounded-3xl bg-[#0A5D54] px-8 py-14 text-center text-[#E8F7F2] sm:py-16"
        >
          <img
            src={APP_ICON}
            alt=""
            aria-hidden="true"
            className="h-16 w-16 rounded-2xl ring-2 ring-white/10"
          />
          <h3
            className="max-w-2xl text-3xl font-black leading-tight tracking-tight sm:text-4xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Ready to meet your brain's new best friend?
          </h3>
          <a
            data-testid="mid-cta-play-button"
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackPlayClick("mid_cta")}
            className="mt-2 inline-flex transition-transform duration-300 hover:-translate-y-1"
            aria-label="Get BrainPet on Google Play"
          >
            <img
              src={PLAY_BADGE}
              alt="Get it on Google Play"
              className="h-16 w-auto drop-shadow-xl"
            />
          </a>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        data-testid="site-footer"
        className="border-t border-black/5 bg-[#DCEEE6]"
      >
        <div className="mx-auto max-w-7xl px-5 py-12 sm:px-10">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-md">
              <div className="flex items-center gap-3">
                <img
                  src={APP_ICON}
                  alt="BrainPet"
                  className="h-11 w-11 rounded-2xl ring-1 ring-black/5"
                />
                <span
                  className="text-xl font-extrabold tracking-tight text-[#0F2E26]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  BrainPet
                </span>
              </div>
              <p
                className="mt-4 text-sm leading-relaxed text-[#3F5A53]"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                A daily brain-training companion. Built with love by VoltGames.
              </p>
            </div>

            <a
              data-testid="footer-play-link"
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackPlayClick("footer")}
              className="inline-flex transition-transform duration-300 hover:-translate-y-0.5"
              aria-label="Get BrainPet on Google Play"
            >
              <img
                src={PLAY_BADGE}
                alt="Get it on Google Play"
                className="h-12 w-auto drop-shadow-md"
              />
            </a>
          </div>

          <div
            className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-black/5 pt-6 text-xs text-[#3F5A53] sm:flex-row sm:items-center"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            <p>© {new Date().getFullYear()} BrainPet · VoltGames. All rights reserved.</p>
            <p>Made with care for curious minds.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}
